import * as React from 'react';
import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Container, alpha, Snackbar, Alert } from '@mui/material';
import { getRequests, getApprovedRequests, getRejectedRequests, clearTutor } from '../../state/slices/tutor';
import { useSelector, useDispatch } from 'react-redux';
import CustomCard from './customCard';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

export default function Request() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [value, setValue] = React.useState(0);
  const dispatch = useDispatch();
  const { requests, successMsg, errorMsg } = useSelector((state) => state.tutor);

  React.useEffect(() => {
    dispatch(getRequests());
  }, [dispatch]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue === 0) {
      dispatch(getRequests());
    } else if (newValue === 1) {
      dispatch(getApprovedRequests());
    } else if (newValue === 2) {
      dispatch(getRejectedRequests());
    }
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Snackbar
        open={Boolean(successMsg || errorMsg)}
        autoHideDuration={3000}
        onClose={() => {
          dispatch(getRequests());
          dispatch(clearTutor());
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={successMsg ? "success" : "error"}
          sx={{ borderRadius: 2 }}
        >
          {successMsg || errorMsg}
        </Alert>
      </Snackbar>

      <Container maxWidth="lg" sx={{ pt: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 4,
            textAlign: 'center',
          }}
        >
          {t('request.myRequests')}
        </Typography>

        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            overflow: 'hidden',
            mb: 3,
          }}
        >
          <AppBar
            position="static"
            sx={{
              bgcolor: 'white',
              boxShadow: 'none',
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  fontSize: '1rem',
                }
              }}
            >
              <Tab label={t('request.pending')} {...a11yProps(0)} />
              <Tab label={t('request.approved')} {...a11yProps(1)} />
              <Tab label={t('request.rejected')} {...a11yProps(2)} />
            </Tabs>
          </AppBar>
        </Box>

        <SwipeableViews
          axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
          index={value}
          onChangeIndex={handleChangeIndex}
        >
          <TabPanel value={value} index={0} dir={theme.direction}>
            <CustomCard requests={requests} value={value} />
          </TabPanel>
          <TabPanel value={value} index={1} dir={theme.direction}>
            <CustomCard requests={requests} value={value} />
          </TabPanel>
          <TabPanel value={value} index={2} dir={theme.direction}>
            <CustomCard requests={requests} value={value} />
          </TabPanel>
        </SwipeableViews>
      </Container>
    </Box>
  );
}
